/**
 * SharePickerModal — the share picker of whoever holds the JD (wireframe: the "+" on the "Draft shared with" row;
 * Ownership design, parts 3 and 8). Lists only the people the server says the drafts may be shared with (an active
 * user whose role can edit JDs, with this JD in their view scope, never the owner, the delegate nor anyone it is
 * already shared with), searchable by name or role. Share stays disabled until someone is picked; with nobody
 * available it says so. Laid out by PersonPickerModal, as the delegate and approver pickers.
 *   open     — shown or not
 *   jobId    — the JD (its job_id)
 *   sharing  — the share is in flight
 *   onCancel()
 *   onShare(email, name)
 *   onRefused({ message }) — the list was refused because the JD changed meanwhile (e.g. SHARE_LIMIT_REACHED,
 *                            NOT_HOLDER): the page says why and reloads, as for every other ownership refusal
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PersonPickerModal from './PersonPickerModal';
import { getShareCandidates } from 'Api/apiService';
import { SHARE_PICKER, TOOLTIP_SHARE_ADD, getShareError } from 'Utils/jdOwnership';

const SharePickerModal = ({
  open, jobId = undefined, sharing = false, onCancel, onShare, onRefused = undefined,
}) => {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    setSelected(null);
    setPeople([]);
    setFailure(null);
    setLoading(true);
    getShareCandidates(jobId)
      .then((data) => { if (!cancelled) setPeople(Array.isArray(data) ? data : []); })
      .catch((err) => {
        if (cancelled) return;
        const refusal = getShareError(err);
        setFailure(err?.response ? refusal.message : SHARE_PICKER.loadFailedMessage);
        if (refusal.reload && onRefused) onRefused(refusal);   // the page closes the picker and reloads
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, jobId]); // eslint-disable-line react-hooks/exhaustive-deps -- onRefused: the parent's latest is enough

  const options = people.map((p) => ({ value: p.value, label: p.role ? `${p.label} (${p.role})` : p.label }));
  const message = failure || (!loading && !people.length ? SHARE_PICKER.noOneMessage : null);
  const pickedName = people.find((p) => p.value === selected)?.label;

  return (
    <PersonPickerModal
      open={open}
      title={SHARE_PICKER.title}
      description={`${TOOLTIP_SHARE_ADD}.`}
      label={SHARE_PICKER.label}
      placeholder={SHARE_PICKER.placeholder}
      options={options}
      selected={selected}
      onSelect={setSelected}
      loading={loading}
      message={message}
      messageTestId="share-picker-message"
      busy={sharing}
      cancelLabel={SHARE_PICKER.cancelLabel}
      confirmLabel={SHARE_PICKER.shareLabel}
      onCancel={onCancel}
      onConfirm={() => onShare(selected, pickedName)}
    />
  );
};

SharePickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  jobId: PropTypes.string,
  sharing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onRefused: PropTypes.func,
};

export default SharePickerModal;
